import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgbDate, NgbDateParserFormatter, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { ComparableDateService } from '@portal/shared/ui/comparable-date-range/src/lib/services/date.service';
import {
  FilterContentItem,
  FILTER_CONTENT_ITEM,
  IFilterToApply,
  ISearchCriteria,
  ISelectInput,
  SearchOperator,
  SearchSelector,
} from '@portal/shared/ui/filter';
import { endOfDay, startOfDay } from 'date-fns';
import { Observable, Subject } from 'rxjs';
import { LocaleDatePipe } from '../../../../../shared/pipes/locale-date/locale-date.pipe';
import { DateFilterQuickPick } from '../../../../../shared/components/filter/date/enums/quick-pick.enum';
import { IFilterDateSelector } from '../../../../../shared/components/filter/date/interfaces/filter-date-selector.interface';
import { DateFilterQuickPicks } from '../../../../../shared/components/filter/date/lists/quick-picks.list';
import { QuickPicks } from '@portal/shared/ui/comparable-date-range/src/lib/enums/date.enum';
import { DatePickerComponent } from '@portal/shared/ui/date-picker/src/lib/date-picker.component';

declare const $localize;

@Component({
  selector: 'portal-transaction-filter-date',
  templateUrl: './date.component.html',
  styleUrls: ['date.component.scss'],
  providers: [
    LocaleDatePipe,
    {
      provide: FILTER_CONTENT_ITEM,
      useExisting: TransactionFilterDateComponent,
    },
  ],
  standalone: false,
})
export class TransactionFilterDateComponent
  extends FilterContentItem
  implements OnChanges, OnInit, AfterViewInit
{
  @Input() selectedEntities = new Map<string, ISearchCriteria>();
  @Input() quickPick = DateFilterQuickPick.Today;
  @Input() showQuickPicks = false;
  @Input() showTimePicker = false;
  @Input() showAnyDateQuickPick = false;
  @Input() maxDate: NgbDate;
  @Input() selectors: IFilterDateSelector[] = [
    {
      id: SearchSelector.Created,
      label: $localize`Created`,
      searchSelectorFrom: SearchSelector.CreatedFrom,
      searchSelectorTo: SearchSelector.CreatedTo,
    },
  ];
  @Input() filterIsUpdated$!: Observable<void>;

  @Output() filterApplied = new EventEmitter<IFilterToApply>();
  @Output() filterUpdated = new EventEmitter<IFilterToApply>();
  @Output() itemToggled = new EventEmitter<number>();
  @Output() quickPickChange = new EventEmitter<DateFilterQuickPick>();
  @ViewChild('datePicker') datePicker: DatePickerComponent;
  @ViewChild('dropdownContent', { static: false }) dropdownContent: ElementRef;

  isAnyDateMode = false;
  isSelectorsVisible = false;
  headerText = $localize`Date`;
  mobileHeaderText = $localize`Date`;
  quickPicksList = this.getQuickPickList();
  dateFilterQuickPickEnum = DateFilterQuickPick;
  selectedQuickPick: DateFilterQuickPick;

  fromDate: NgbDate;
  toDate: NgbDate;
  fromTime: NgbTimeStruct;
  toTime: NgbTimeStruct;
  datePickerErrors: string[];
  radioName = 'dateFilterRadioSelector';
  selectedRadioMap = new Map<string, ISelectInput>();
  intermediateSelectedRadioMap = new Map<string, ISelectInput>();
  intermediateFromDate: NgbDate;
  intermediateToDate: NgbDate;
  intermediateFromTime: NgbTimeStruct;
  intermediateToTime: NgbTimeStruct;
  intermediateQuickPick: DateFilterQuickPick;
  refreshRadioButton$ = new Subject<void>();

  private selectedSelector: IFilterDateSelector;

  constructor(
    private formatter: NgbDateParserFormatter,
    private dateFilterQuickPicks: DateFilterQuickPicks,
    private comparableDateService: ComparableDateService,
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this.updateDateQuickPick();
  }

  ngOnInit(): void {
    this.filterIsUpdated$?.subscribe(() => {
      this.onQuickPickSelect(this.quickPick);
      this.updateDateQuickPick();
      this.update();
      this.updateDates();
      this.refreshRadioButton$.next();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.quickPick) {
      this.onQuickPickSelect(this.quickPick);
      this.updateDateQuickPick();
      if (this.datePicker) {
        this.datePicker.setQuickPickButtonText(this.quickPick);
        this.datePicker.selectedQuickPick = this.quickPick;
      }
    }

    if (changes?.showAnyDateQuickPick) {
      this.quickPicksList = this.getQuickPickList();
    }

    if (changes?.selectors) {
      this.selectedSelector = this.selectors[0];
    }

    this.update();
    this.refreshRadioButton$.next();
  }

  onFilterApplied(): void {
    this.selectedRadioMap.clear();
    this.intermediateSelectedRadioMap.forEach((selector) => {
      this.selectedRadioMap.set(selector.id, {
        id: selector.id,
        text: selector.text,
        key: selector.id,
      });
    });

    this.toDate = this.intermediateToDate;
    this.fromDate = this.intermediateFromDate;
    this.toTime = this.intermediateToTime;
    this.fromTime = this.intermediateFromTime;
    this.selectedQuickPick = this.intermediateQuickPick;
    this.updateDateHeaderText();
    this.filterUpdate();
    this.itemToggled.emit();
  }

  filterUpdate(): void {
    if (this.isAnyDateMode) {
      this.quickPickUpdate();
      return;
    }

    this.quickPickChange.emit(this.selectedQuickPick);

    const selectorsToRemove = this.selectedSelector?.id
      ? this.selectors.filter((selector) => selector.id !== this.selectedSelector?.id)
      : [];
    selectorsToRemove.forEach((selector) => {
      this.filterUpdated.emit({
        key: selector.searchSelectorFrom,
        toRemove: true,
      });
      this.filterUpdated.emit({
        key: selector.searchSelectorTo,
        toRemove: true,
      });
    });

    if (this.selectedSelector) {
      if (this.fromDate) {
        this.filterUpdated.emit({
          key: this.selectedSelector.searchSelectorFrom,
          operator: SearchOperator.GreaterThanAndEqualTo,
          selector: this.selectedSelector.searchSelectorFrom,
          argument: this.fromTime
            ? this.ngbDateToDate(this.fromDate, { ...this.fromTime, milliseconds: 0 }).toISOString()
            : startOfDay(this.ngbDateToDate(this.fromDate)).toISOString(),
        });
      }

      if (this.toDate) {
        this.filterUpdated.emit({
          key: this.selectedSelector.searchSelectorTo,
          operator: SearchOperator.LessThanAndEqualTo,
          selector: this.selectedSelector.searchSelectorTo,
          argument: this.toTime
            ? this.ngbDateToDate(this.toDate, {
                ...this.toTime,
                second: 59,
                milliseconds: 999,
              }).toISOString()
            : endOfDay(this.ngbDateToDate(this.toDate)).toISOString(),
        });
      }
    }
  }

  getList(): Observable<ISelectInput[]> {
    this.update();
    this.refreshRadioButton$.next();
    this.datePicker.setQuickPickButtonText(this.quickPick);
    return null;
  }

  onRadioSelected(selector: IFilterDateSelector): void {
    if (selector) {
      this.selectedSelector = selector;
      this.intermediateSelectedRadioMap.clear();
      this.intermediateSelectedRadioMap.set(selector.id, {
        id: selector.id,
        text: selector.label,
        key: selector.id,
      });
    }
  }

  onFromDateApplied(fromDate: NgbDate): void {
    this.intermediateFromDate = fromDate;
  }

  onToDateApplied(toDate: NgbDate): void {
    this.intermediateToDate = toDate;
  }

  onFromTimeApplied(fromTime: NgbTimeStruct): void {
    if (this.showTimePicker) {
      this.intermediateFromTime = fromTime;
    }
  }

  onToTimeApplied(toTime: NgbTimeStruct): void {
    if (this.showTimePicker) {
      this.intermediateToTime = toTime;
    }
  }

  onDatePickerErrors(datePickerErrors: string[]): void {
    this.datePickerErrors = datePickerErrors;
  }

  onQuickPickSelect(quickPickId: DateFilterQuickPick): void {
    this.intermediateQuickPick = quickPickId;
    this.setDateRange(quickPickId);
  }

  areInputsValid(): boolean {
    return Boolean(
      this.toDate && this.fromDate && this.selectedSelector && !this.datePickerErrors?.length,
    );
  }

  clear(): void {
    this.isOpen = false;
  }

  onFilterClick(): void {
    this.itemToggled.emit();
    this.dropdownContent.nativeElement.scrollTop = 0;
  }

  private update(): void {
    this.updateSelector();
    this.updateDates();
    this.updateSelectorsVisibility();
    this.updateDateHeaderText();
  }

  private updateDateQuickPick(): void {
    if (this.datePicker && this.quickPick) {
      this.datePicker.setQuickPickButtonText(this.quickPick);
      this.datePicker.selectedQuickPick = this.quickPick;
    }
  }

  private quickPickUpdate(isApply = false): void {
    this.quickPickChange.emit(DateFilterQuickPick.AnyDate);
    if (isApply) {
      this.itemToggled.emit();
    }
  }

  private updateSelector(): void {
    const entitiesSelector = this.selectors.find((selector) =>
      this.selectedEntities.has(selector.searchSelectorFrom),
    );
    this.onRadioSelected(entitiesSelector);
  }

  private updateDates(): void {
    if (this.selectedEntities?.size) {
      const entities = Array.from(this.selectedEntities.values());
      const selectorDateFrom = entities.find(
        (entity) => entity.selector === this.selectedSelector?.searchSelectorFrom,
      );
      const selectorDateTo = entities.find(
        (entity) => entity.selector === this.selectedSelector?.searchSelectorTo,
      );

      if (selectorDateFrom && selectorDateTo) {
        this.setDates(selectorDateFrom.argument, selectorDateTo.argument);
      }
    }
  }

  private updateDateHeaderText(): void {
    let selectorPrefix = '';
    selectorPrefix = `${this.selectedSelector?.label}: `;

    let dateText = '';
    if (this.isAnyDateMode) {
      dateText = $localize`:date filter option|:Any time`;
    } else if (this.fromDate?.equals(this.toDate)) {
      dateText = `${this.formatter.format(this.fromDate)} ${this.padZero(
        this.fromTime?.hour ?? 0,
      )}:${this.padZero(this.fromTime?.minute ?? 0)} - ${this.padZero(
        this.toTime?.hour ?? 23,
      )}:${this.padZero(this.toTime?.minute ?? 59)}`;
    } else {
      dateText = `${this.formatter.format(this.fromDate)} ${this.padZero(
        this.fromTime?.hour ?? 0,
      )}:${this.padZero(this.fromTime?.minute ?? 0)} - ${this.formatter.format(
        this.toDate,
      )} ${this.padZero(this.toTime?.hour ?? 23)}:${this.padZero(this.toTime?.minute ?? 59)}`;
    }

    this.headerText = `${selectorPrefix}${dateText}`;
    this.mobileHeaderText = `${dateText}`;
  }

  private padZero(number: number): string {
    return number.toString().padStart(2, '0');
  }

  private updateIsAnyDateMode(quickPick: DateFilterQuickPick): void {
    this.isAnyDateMode = quickPick === DateFilterQuickPick.AnyDate;
  }

  private updateSelectorsVisibility(): void {
    this.isSelectorsVisible = this.selectors.length > 1;
  }

  private setDates(fromDateString: string, toDateString: string): void {
    this.fromDate = this.intermediateFromDate = this.getNgbDate(fromDateString);
    this.toDate = this.intermediateToDate = this.getNgbDate(toDateString);
    this.fromTime = this.intermediateFromTime = this.getNgbTime(fromDateString);
    this.toTime = this.intermediateToTime = this.getNgbTime(toDateString);
  }

  private setDateRange(quickPickId: DateFilterQuickPick | QuickPicks): void {
    const dateRange = this.comparableDateService.getQuickPickDateRange(quickPickId);
    if (dateRange) {
      this.intermediateFromDate = this.getNgbDate(dateRange.start);
      this.intermediateToDate = this.getNgbDate(dateRange.end);
      this.intermediateFromTime = this.getNgbTime(dateRange.start);
      this.intermediateToTime = this.getNgbTime(dateRange.end);
    }
  }

  private getNgbDate(dateString: string): NgbDate {
    const date = new Date(dateString);
    return NgbDate.from({
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    });
  }

  private getNgbTime(dateString: string): NgbTimeStruct {
    const date = new Date(dateString);
    return {
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
    };
  }

  private ngbDateToDate(
    ngbDate: NgbDate,
    ngbTime: { hour: number; minute: number; second: number; milliseconds: number } = {
      hour: 0,
      minute: 0,
      second: 0,
      milliseconds: 0,
    },
  ): Date {
    return new Date(
      ngbDate.year,
      ngbDate.month - 1,
      ngbDate.day,
      ngbTime.hour,
      ngbTime.minute,
      ngbTime.second,
      ngbTime.milliseconds,
    );
  }

  private getQuickPickList(): ISelectInput[] {
    return this.dateFilterQuickPicks.list.filter(({ id: quickPickId }) => {
      if (!this.showAnyDateQuickPick) {
        return quickPickId !== DateFilterQuickPick.AnyDate;
      }

      return true;
    });
  }
}
